import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

/**
 * Modal primitive. phr has no UI kit, so this is hand-rolled.
 *
 * Portalled to document.body on purpose: the dashboard's .appointments-table is
 * overflow-x:auto, and an absolutely-positioned panel inside that ancestor gets
 * clipped by it.
 */
const Dialog = ({ title, onClose, dismissible, width, children }) => {
  const panelRef = useRef(null);
  const lastFocused = useRef(null);

  useEffect(() => {
    lastFocused.current = document.activeElement;

    const onKey = (e) => {
      if (e.key === "Escape" && dismissible) onClose();
      if (e.key !== "Tab" || !panelRef.current) return;

      // Focus trap: a money dialog must not let Tab wander to the page behind.
      const focusable = panelRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    const rootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    // The page scrolls on <html>: phrResponsive's `html { overflow-x: hidden }`
    // makes its overflow-y compute to auto, so body's overflow never reaches the
    // viewport and a lock on <body> alone let the page scroll behind the dialog.
    document.documentElement.style.overflow = "hidden";

    // Focus the text field if there is one, otherwise the panel itself — never
    // a button. The first button in the DOM is the close X (focus ring on the
    // wrong thing), and the first *action* is usually destructive, which Enter
    // would then fire.
    if (panelRef.current) {
      const input = panelRef.current.querySelector("input");
      (input || panelRef.current).focus();
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      document.documentElement.style.overflow = rootOverflow;
      if (lastFocused.current && lastFocused.current.focus) {
        lastFocused.current.focus();
      }
    };
  }, [onClose, dismissible]);

  return ReactDOM.createPortal(
    <div
      className="zy-dialog-overlay"
      // Backdrop click closes only when dismissible — mid-flight requests must
      // not be abandoned by a stray click.
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="zy-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        tabIndex={-1}
        style={width ? { maxWidth: width } : undefined}
      >
        <div className="zy-dialog-head">
          <h2>{title}</h2>
          {dismissible && (
            <button
              type="button"
              className="zy-dialog-close"
              aria-label="Close"
              onClick={onClose}
            >
              &times;
            </button>
          )}
        </div>
        <div className="zy-dialog-body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

Dialog.propTypes = {
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  /** False while a request is in flight — no Escape, no backdrop, no close X. */
  dismissible: PropTypes.bool,
  width: PropTypes.string,
  children: PropTypes.node,
};

Dialog.defaultProps = { dismissible: true };

export default Dialog;
